import { useState } from "react";
import { Heart, Reply, Trash2, MoreHorizontal } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { Comment } from "@/lib/api";
import clsx from "clsx";

function truncateAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// Matches 0x addresses — full (0xAbCd...42 hex chars) or truncated (0xAbCd...1234).
const ADDRESS_PATTERN = /0x[a-fA-F0-9]+(?:\.\.\.[a-fA-F0-9]+)?/g;

function renderBody(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const re = new RegExp(ADDRESS_PATTERN.source, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span key={match.index} className="text-orange font-mono">
        {match[0]}
      </span>,
    );
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

interface CommentItemProps {
  comment: Comment;
  canInteract: boolean;
  onLike: (commentId: string) => void;
  onReply?: (commentId: string, commenterAddress: string) => void;
  onDelete?: (commentId: string) => void;
  walletAddress?: string;
  writerAddress?: string;
  isReply?: boolean;
}

export function CommentItem({
  comment,
  canInteract,
  onLike,
  onReply,
  onDelete,
  walletAddress,
  writerAddress,
  isReply = false,
}: CommentItemProps) {
  const [liked, setLiked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isOwner = walletAddress && comment.commenterAddress.toLowerCase() === walletAddress.toLowerCase();
  const isAuthor = writerAddress && comment.commenterAddress.toLowerCase() === writerAddress.toLowerCase();

  const handleLike = () => {
    if (!canInteract || liked) return;
    setLiked(true);
    onLike(comment.id);
  };

  return (
    <div className={clsx("py-3", isReply && "pl-8 border-l-2 border-beige/20")}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-grey">
            {truncateAddress(comment.commenterAddress)}
          </span>
          {isAuthor && (
            <span className="text-[10px] font-body font-semibold text-orange bg-orange/10 px-1.5 py-0.5 rounded">
              Author
            </span>
          )}
          <span className="text-xs text-beige">
            {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
          </span>
        </div>
        {isOwner && onDelete && (
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1 rounded text-beige hover:text-grey transition-colors"
            >
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 bg-white border border-beige/30 rounded-lg shadow-lg z-20">
                  <button
                    onClick={() => { onDelete(comment.id); setMenuOpen(false); }}
                    className="flex items-center gap-2 px-3 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors whitespace-nowrap"
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <p className="text-sm font-body text-black mb-2 whitespace-pre-wrap break-words">
        {renderBody(comment.body)}
      </p>
      <div className="flex items-center gap-4">
        {canInteract && (
          <button
            onClick={handleLike}
            disabled={liked}
            className={clsx(
              "flex items-center gap-1 text-xs transition-colors",
              liked ? "text-orange" : "text-grey hover:text-orange",
            )}
          >
            <Heart size={14} fill={liked ? "currentColor" : "none"} />
            {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
          </button>
        )}
        {!canInteract && comment.likeCount > 0 && (
          <span className="flex items-center gap-1 text-xs text-grey">
            <Heart size={14} />
            {comment.likeCount}
          </span>
        )}
        {!isReply && onReply && canInteract && (
          <button
            onClick={() => onReply(comment.id, comment.commenterAddress)}
            className="flex items-center gap-1 text-xs text-grey hover:text-orange transition-colors"
          >
            <Reply size={14} />
            Reply
          </button>
        )}
      </div>
      {comment.replies?.length > 0 && (
        <div className="mt-2">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              canInteract={canInteract}
              onLike={onLike}
              onDelete={onDelete}
              walletAddress={walletAddress}
              writerAddress={writerAddress}
              isReply
            />
          ))}
        </div>
      )}
    </div>
  );
}
